import type { ReactNode } from 'react'

import { Loader, Tooltip, UnstyledButton } from '@mantine/core'
import { observer } from 'mobx-react-lite'

import { getRevisionsState } from '#choo-app/lib/editor/plugins/revisions'
import { ChangeTrackingStatusPill } from '#components/ChangeTracking/StatusPill'
import { FaIcon } from '#components/FaIcon'
import { StatusBar } from '#components/StatusBar'
import { useMst } from '#state'
import { getPageCount } from '#util/prosemirrorHelpers'

import { InkMessagesMenu } from './InkMessagesMenu'
import { PageCount } from './PageCount'
import { PagelessToggle } from './PagelessToggle'
import { RevisionPill } from './RevisionPill'
import { RevisionsStatus } from './RevisionsStatus'
import { RundownLinkIcon } from './RundownLinkIcon'
import { ScriptStatusButton } from './ScriptStatusButton'
import { SyntaxHighlighting } from './SyntaxHighlighting'
import { showViewPreferencesModal } from './ViewPreferencesModal'
import { WrapText } from './WrapText'
import { ZoomMenu } from './ZoomMenu'

interface ScriptStatusBarProps {
  openRevisionsModal(): void
}

export const ScriptStatusBar = observer(function ScriptStatusBar({
  openRevisionsModal,
}: ScriptStatusBarProps) {
  const {
    currentOrg,
    currentRundown,
    currentScript,
    user,
    currentInkProject,
    view,
  } = useMst()
  if (!currentScript) return null

  const { isInk, isLinkedToRundown, isPrivate } = currentScript
  const { pageless } = user.prefs
  const showScriptStatus = isPrivate || currentOrg?.hasLimitedScriptsEnabled
  const maxMenuHeight = view.dimensions.scriptScroller.height - 20

  const editorState = currentScript.pmEditor.editorState
  const pageCount = editorState ? getPageCount(editorState) : null
  const showCompilationStatus = isInk && currentInkProject
  const revisionsPluginState = currentScript.pmEditor.editorState
    ? getRevisionsState(currentScript.pmEditor.editorState)
    : undefined

  return (
    <StatusBar>
      <StatusBar.Section>
        {!isInk && (
          <PagelessToggle
            isPageless={pageless}
            onEnablePageless={user.enablePagelessMode}
            onDisablePageless={user.disablePagelessMode}
          />
        )}
        <ZoomMenu />
        {isInk && <WrapText />}
        <StatusBar.ActionIcon
          onClick={showViewPreferencesModal}
          tooltip="View preferences"
          btnVariant="subtle"
          icon={<FaIcon icon="fa-user-gear" />}
        />
        {isInk && <SyntaxHighlighting />}
      </StatusBar.Section>
      <StatusBar.Section>
        {currentRundown && <RundownLinkIcon isLinked={isLinkedToRundown} />}
        {currentScript.trackingChanges && <ChangeTrackingStatusPill />}
        {revisionsPluginState?.tag === 'Loading' &&
          revisionsPluginState.revisionsState.enabled && (
            <RevisionPill
              pageColor="#FAFAFA"
              textColor="dimmed"
              label="Loading revisions…"
            />
          )}
        {currentOrg?.revisionsEnabled &&
          revisionsPluginState?.tag === 'Resolved' && (
            <OptionalButton
              disabled={!user.canManageRevisions}
              onClick={openRevisionsModal}
            >
              <RevisionsStatus
                revisionsState={revisionsPluginState.revisionsState}
              />
            </OptionalButton>
          )}
        {pageCount !== null && <PageCount count={pageCount} />}
        {showCompilationStatus && (
          <>
            {currentInkProject.status === 'working' && (
              <Tooltip label="Compiling...">
                <div>
                  <Loader size="xs" color="yellow" />
                </div>
              </Tooltip>
            )}
            <InkMessagesMenu
              maxHeight={maxMenuHeight}
              inkProject={currentInkProject}
            />
          </>
        )}
        {showScriptStatus && (
          <ScriptStatusButton
            readOnly={!user.canChangeScriptStatus}
            script={currentScript}
          />
        )}
      </StatusBar.Section>
    </StatusBar>
  )
})

interface OptionalButtonProps {
  children: ReactNode
  disabled: boolean
  onClick: () => void
}

function OptionalButton(props: OptionalButtonProps) {
  if (props.disabled) return <>{props.children}</>

  return (
    <UnstyledButton onClick={props.onClick}>{props.children}</UnstyledButton>
  )
}
